import { isDefined, isString } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import type { SubmitOrderResponse, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export type UpdateTakeProfitRequest = {
  tileId: string;

  orderRef: string;
  way: ClientWay;
  currency1: string;
  currency2: string;
  limitPrice?: number;
  customerPrice?: number;
  margin?: number;
  notional?: number;
  notionalCurrencyIndex: CurrencyIndex;
  startDateUtc: string | null;
  endDateUtc: string | null;
};

export type UpdateTakeProfitBody = {
  orderRef: string;
  amountInCcy1?: number;
  amountInCcy2?: number;
  ccyPair: string; // 'EUR/USD';
  way: ClientWay;
  limitPrice: string; // '1';
  margin: number; // 11.5;
  customerPrice: string; // '0.99885';
  startDateUtc: string | null;
  endDateUtc: string | null;
};

export const updateTakeProfit = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SubmitOrderResponse, UpdateTakeProfitRequest>({
    query: (request) => ({
      url: '/oms/update/TakeProfit',
      method: 'POST',
      body: generateBody(request),
      timeout: 10000,
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformResponse: (response: string) => {
      return {
        type: 'success',
        orderId: response,
      };
    },
    transformErrorResponse: (errorResponse, _meta, arg): SubmitOrderResponse => {
      const tileId = arg.tileId;
      if (errorResponse.status === 'TIMEOUT_ERROR' || errorResponse.status === 504) {
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'timeout',
              errorCode: -1,
            },
          ],
        };
      }

      const data = errorResponse.data as
        | string
        | { validationResponseDetailContent: ValidationResponseDetailContent[] };
      if (typeof errorResponse.status === 'number' && errorResponse.status >= 400 && errorResponse.status <= 499) {
        logger.logError(
          `Order submission failed because of a validation field rejection. orderId: {tileId_s},
                response: {data_s}`,
          tileId,
          JSON.stringify(data),
        );
        if (isValidateOrderResponse(data)) {
          return {
            type: 'failure',
            errors: data?.validationResponseDetailContent,
          };
        }

        if (isString(data)) {
          return {
            type: 'failure',
            errors: [
              {
                field: 'unknown',
                errorDescription: data,
                errorCode: -1,
              },
            ],
          };
        }
      }

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 500 && errorResponse.status <= 599) {
        logger.logError(
          'Backend crashed with error 500 for orderId: {tileId_s}, response: {data_s}',
          tileId,
          JSON.stringify(data),
        );
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'server.error',
              errorCode: -1,
            },
          ],
        };
      }

      logger.logError(
        'Backend returned an unexpected error {status_s} for orderId: {tileId_s}, response: {data_s}',
        errorResponse.status,
        tileId,
        JSON.stringify(data),
      );

      return {
        type: 'failure',
        errors: [
          {
            field: 'unknown',
            errorDescription: 'order.unexpected',
            errorCode: -1,
          },
        ],
      };
    },
  });
};

const generateBody = (request: UpdateTakeProfitRequest): UpdateTakeProfitBody => {
  // Here you can add any additional processing of the request body if needed
  return {
    orderRef: request.orderRef,
    amountInCcy1: request.notionalCurrencyIndex === 1 ? request.notional : undefined,
    amountInCcy2: request.notionalCurrencyIndex === 2 ? request.notional : undefined,
    ccyPair: `${request.currency1}/${request.currency2}`,
    startDateUtc: isDefined(request.startDateUtc) ? request.startDateUtc : '',
    endDateUtc: isDefined(request.endDateUtc) ? request.endDateUtc : '',
    way: request.way,
    limitPrice: isDefined(request.limitPrice) ? String(request.limitPrice) : '',
    margin: request.margin ?? 0,
    customerPrice: isDefined(request.customerPrice) ? String(request.customerPrice) : '',
  };
};

function isValidateOrderResponse(
  response: unknown,
): response is { validationResponseDetailContent: ValidationResponseDetailContent[]; isReadyToSubmit: boolean } {
  return (
    typeof response === 'object' &&
    response !== null &&
    'validationResponseDetailContent' in response &&
    'isReadyToSubmit' in response &&
    Array.isArray(response.validationResponseDetailContent)
  );
}
